import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('quru_token'));

  const fetchMe = useCallback(async (t) => {
    try {
      const res = await api.get('/auth/me', { headers: { Authorization: `Bearer ${t}` } });
      setUser(res.data);
    } catch {
      setUser(null);
      setToken(null);
      localStorage.removeItem('quru_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token && !user) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchMe(token);
    } else if (!token) {
      setLoading(false);
    }
  }, [token, fetchMe]);

  const login = async (email, password, totpCode) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    const res = await api.post('/auth/login', payload);
    const data = res.data;
    // MFA required — return indicator, no token yet
    if (data.requires_mfa) {
      return { requires_mfa: true };
    }
    const { token: t } = data;
    localStorage.setItem('quru_token', t);
    api.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    setToken(t);
    // Fetch full user (with permissions) before setting user to avoid flash
    await fetchMe(t);
    return true;
  };

  const logout = () => {
    // Log the event before clearing token
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem('quru_token');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {}
  };

  const hasPermission = (perm) => user?.permissions?.includes(perm) ?? false;
  const hasModule = (mod) => user?.modules?.includes(mod) ?? false;
  const isAdmin = user?.role === 'admin';
  const canWrite = user?.role !== 'visor';
  const isVisor = user?.role === 'visor';

  // Granular permission check: can('viviendas', 'edit')
  const can = (module, action = 'view') => {
    const matrix = user?.permissions_matrix;
    if (!matrix) return isAdmin; // fallback
    const modPerms = matrix[module];
    if (!modPerms) return false;
    return modPerms.includes(action);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, hasPermission, hasModule, isAdmin, canWrite, isVisor, can, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
